import React, { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  Select,
  useToast,
  Text,
  Icon,
} from "@chakra-ui/react";
import { FaCheckCircle } from "react-icons/fa";
import axios from "axios";

const ContactFormModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    companyName: "",
    employeeCount: "",
    message: "",
    enquiryType: "sales",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const toast = useToast();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(
        `${
          process.env.REACT_APP_API_URL || "http://localhost:3001"
        }/api/contact-enquiry`,
        formData
      );

      if (response.data.success) {
        setIsSuccess(true);
        toast({
          title: "Enquiry Submitted!",
          description: "Our team will contact you shortly.",
          status: "success",
          duration: 5000,
          isClosable: true,
          position: "top",
        });

        // Reset form after 2 seconds and close modal
        setTimeout(() => {
          setFormData({
            name: "",
            email: "",
            phone: "",
            companyName: "",
            employeeCount: "",
            message: "",
            enquiryType: "sales",
          });
          setIsSuccess(false);
          onClose();
        }, 2000);
      }
    } catch (error) {
      toast({
        title: "Submission Failed",
        description: error.response?.data?.message || "Please try again later.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent>
        <ModalHeader
          bgGradient="linear(to-r, purple.500, purple.600)"
          color="white"
          borderTopRadius="md"
        >
          Contact Us
        </ModalHeader>
        <ModalCloseButton color="white" />

        {isSuccess ? (
          <ModalBody py={12}>
            <VStack spacing={4} textAlign="center">
              <Icon as={FaCheckCircle} boxSize={16} color="green.500" />
              <Text fontSize="xl" fontWeight="bold" color="gray.800">
                Thank You!
              </Text>
              <Text color="gray.600">
                We've received your enquiry and will get back to you shortly.
              </Text>
            </VStack>
          </ModalBody>
        ) : (
          <form onSubmit={handleSubmit}>
            <ModalBody pt={6}>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="600">
                    Full Name
                  </FormLabel>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    focusBorderColor="purple.500"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="600">
                    Email Address
                  </FormLabel>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@company.com"
                    focusBorderColor="purple.500"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="600">
                    Phone Number
                  </FormLabel>
                  <Input
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+91 98765 43210"
                    focusBorderColor="purple.500"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="600">
                    Company Name
                  </FormLabel>
                  <Input
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    placeholder="Your Company"
                    focusBorderColor="purple.500"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="600">
                    Number of Employees
                  </FormLabel>
                  <Select
                    name="employeeCount"
                    value={formData.employeeCount}
                    onChange={handleChange}
                    placeholder="Select employee count"
                    focusBorderColor="purple.500"
                  >
                    <option value="1-50">1-50</option>
                    <option value="51-200">51-200</option>
                    <option value="201-500">201-500</option>
                    <option value="501-1000">501-1000</option>
                    <option value="1000+">1000+</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="600">
                    Enquiry Type
                  </FormLabel>
                  <Select
                    name="enquiryType"
                    value={formData.enquiryType}
                    onChange={handleChange}
                    focusBorderColor="purple.500"
                  >
                    <option value="sales">Sales Enquiry</option>
                    <option value="demo">Request Demo</option>
                    <option value="support">Technical Support</option>
                    <option value="partnership">Partnership</option>
                    <option value="other">Other</option>
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="600">
                    Message
                  </FormLabel>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your requirements..."
                    rows={4}
                    focusBorderColor="purple.500"
                  />
                </FormControl>
              </VStack>
            </ModalBody>

            <ModalFooter>
              <Button
                variant="ghost"
                mr={3}
                onClick={onClose}
                isDisabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                bgGradient="linear(to-r, purple.500, purple.600)"
                color="white"
                type="submit"
                isLoading={isLoading}
                loadingText="Submitting..."
                _hover={{
                  bgGradient: "linear(to-r, purple.600, purple.700)",
                }}
              >
                Submit Enquiry
              </Button>
            </ModalFooter>
          </form>
        )}
      </ModalContent>
    </Modal>
  );
};

export default ContactFormModal;
